import { Download, FileUp, Images } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { telechargerAnomaliesImportHrms, telechargerModeleHrms, useATraiterHrms, useActionsHrms, useDossiersHrms, useImportsHrms, useRapportImportHrms, useStructures } from '@/api/hooks';
import { LIBELLE_LIEN_CHARGE, LIBELLE_MODE_PAIEMENT_HRMS, LIBELLE_STATUT_DOSSIER_HRMS, type RapportImportHrms, type StatutDossierHrms } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { PhotoAgent, StatutDossierHrmsBadge } from '@/components/hrms';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dateFr, dateHeureFr } from '@/lib/utils';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Erreur inattendue.');
const ONGLETS = ['dossiers', 'a-traiter', 'import', 'photos'] as const;
type Onglet = (typeof ONGLETS)[number];
const LIBELLE_ONGLET: Record<Onglet, string> = { dossiers: 'Dossiers', 'a-traiter': 'À traiter', import: 'Import du fichier HRMS', photos: 'Photos' };

/**
 * Référentiel HRMS, gestion (spécifications HRMS et Paie, phase 1, § 5.1 et § 6 ; lot HP-1) : dossiers et anomalies,
 * file de travail (corrections, paiements à valider, photos, personnes à charge), importation du fichier HRMS avec
 * rapport d'anomalies, photos en lot.
 */
export function GestionHrms() {
  const [params, setParams] = useSearchParams();
  const u = useSession()!.utilisateur;
  const rh = u.profils.some((p) => p === 'DIRECTEUR_RH' || p === 'RESSOURCES_HUMAINES');
  const importateur = rh || u.profils.includes('ADMINISTRATEUR');
  const onglet: Onglet = ONGLETS.includes(params.get('onglet') as Onglet) ? (params.get('onglet') as Onglet) : 'dossiers';
  const changer = (cle: string, valeur: string) => {
    const n = new URLSearchParams(params);
    if (valeur) n.set(cle, valeur);
    else n.delete(cle);
    setParams(n, { replace: true });
  };
  return (
    <div className="space-y-6">
      <PageHeader titre="Référentiel HRMS" sousTitre="Dossier unique de chaque agent : identité, affectations, contrats, situation sociale, coordonnées de paiement (double contrôle), photo ; importation du fichier HRMS et régularisation des anomalies." />
      <Tabs value={onglet} onValueChange={(v) => changer('onglet', v)}>
        <TabsList className="flex-wrap">
          {ONGLETS.filter((o) => (o === 'a-traiter' ? rh : o === 'import' || o === 'photos' ? importateur : true)).map((o) => (
            <TabsTrigger key={o} value={o}>
              {LIBELLE_ONGLET[o]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {onglet === 'dossiers' && <Dossiers structure={params.get('structure') ?? ''} statut={params.get('statut') ?? ''} q={params.get('q') ?? ''} anomalies={params.get('anomalies') ?? ''} changer={changer} />}
      {onglet === 'a-traiter' && rh && <ATraiter />}
      {onglet === 'import' && importateur && <ImportHrms />}
      {onglet === 'photos' && importateur && <ImportPhotos />}
    </div>
  );
}

function Dossiers({ structure, statut, q, anomalies, changer }: { structure: string; statut: string; q: string; anomalies: string; changer: (cle: string, valeur: string) => void }) {
  const structures = useStructures();
  const { data, isLoading, error } = useDossiersHrms({ structure: structure || undefined, statut: statut || undefined, q: q || undefined, anomalies: anomalies || undefined });
  const total = data?.length ?? 0;
  const compteur = (s: StatutDossierHrms) => data?.filter((d) => d.statut === s).length ?? 0;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Dossiers" valeur={total} detail={`${data?.filter((d) => d.photo === 'VALIDEE').length ?? 0} photo(s) validée(s)`} />
        <Kpi label="À régulariser" valeur={compteur('A_REGULARISER')} detail="anomalies bloquantes" accent={compteur('A_REGULARISER') > 0 ? 'alerte' : 'ok'} />
        <Kpi label="Contrôlés" valeur={compteur('CONTROLE')} detail="en attente de validation du DRH" />
        <Kpi label="Validés" valeur={compteur('VALIDE')} detail={`${data?.filter((d) => d.paiementValide).length ?? 0} paiement(s) validé(s)`} accent="ok" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Input className="w-64" placeholder="Nom, matricule, fonction…" value={q} onChange={(e) => changer('q', e.target.value)} />
        <SelectNatif value={structure} onChange={(e) => changer('structure', e.target.value)}>
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={statut} onChange={(e) => changer('statut', e.target.value)}>
          <option value="">Tous les statuts</option>
          {(Object.keys(LIBELLE_STATUT_DOSSIER_HRMS) as StatutDossierHrms[]).map((s) => (
            <option key={s} value={s}>
              {LIBELLE_STATUT_DOSSIER_HRMS[s]}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif value={anomalies} onChange={(e) => changer('anomalies', e.target.value)}>
          <option value="">Avec ou sans anomalie</option>
          <option value="true">Avec anomalies</option>
          <option value="false">Sans anomalie</option>
        </SelectNatif>
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucun dossier ne correspond." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table data-testid="dossiers-hrms">
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Fonction et grade</TableHead>
                <TableHead>Structure</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Anomalies</TableHead>
                <TableHead className="hidden lg:table-cell">Paiement</TableHead>
                <TableHead className="hidden lg:table-cell">Importé</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.agent.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <PhotoAgent agentId={d.agent.id} version={d.photo ? d.agent.id : null} nom={d.agent.nom} prenom={d.agent.prenom} taille="sm" />
                      <div>
                        <Link to={`/rh/agents/${d.agent.id}/dossier`} className="font-medium text-primary hover:underline">
                          {d.agent.prenom} {d.agent.nom}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          {d.agent.matricule}
                          {d.photo === 'A_VALIDER' ? ' · photo à valider' : d.photo ? '' : ' · sans photo'}
                          {d.correctionsOuvertes ? ` · ${d.correctionsOuvertes} correction(s)` : ''}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {d.agent.fonction}
                    {d.agent.grade && <div className="text-xs text-muted-foreground">{d.agent.grade}</div>}
                  </TableCell>
                  <TableCell>{d.agent.structure?.code ?? '—'}</TableCell>
                  <TableCell>
                    <StatutDossierHrmsBadge statut={d.statut} />
                  </TableCell>
                  <TableCell>
                    {d.anomalies ? (
                      <span className={d.bloquantes ? 'text-destructive' : 'text-amber-700'}>
                        {d.anomalies}
                        {d.bloquantes ? ` (${d.bloquantes} bloquante${d.bloquantes > 1 ? 's' : ''})` : ''}
                      </span>
                    ) : (
                      <span className="text-emerald-700">aucune</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">{d.paiementValide ? <Badge variant="outline">validé</Badge> : <span className="text-amber-700">à valider</span>}</TableCell>
                  <TableCell className="hidden lg:table-cell">{d.importeLe ? dateFr(d.importeLe) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function ATraiter() {
  const { data, isLoading, error } = useATraiterHrms();
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const lien = (id: string, onglet: string) => `/rh/agents/${id}/dossier?onglet=${onglet}`;
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="hrms-a-traiter">
      <Card>
        <CardHeader>
          <CardTitle>
            Demandes de correction <Badge variant="secondary">{data.corrections.length}</Badge>
          </CardTitle>
          <CardDescription>Déposées par les agents sur leur dossier : à accepter (puis corriger) ou à refuser avec réponse.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.corrections.length ? (
            <Vide message="Aucune demande ouverte." />
          ) : (
            <ul className="divide-y text-sm">
              {data.corrections.map((c) => (
                <li key={c.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                  <span>
                    <Link to={lien(c.agent.id, 'corrections')} className="font-medium text-primary hover:underline">
                      {c.agent.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {c.libelleSection} · {c.champ} → {c.valeurProposee}
                    </div>
                  </span>
                  <span className="text-xs text-muted-foreground">{dateHeureFr(c.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>
            Coordonnées de paiement à valider <Badge variant="secondary">{data.paiements.length}</Badge>
          </CardTitle>
          <CardDescription>Saisies par les RH, validées par le Directeur RH (double contrôle).</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.paiements.length ? (
            <Vide message="Aucune coordonnée en attente." />
          ) : (
            <ul className="divide-y text-sm">
              {data.paiements.map((p) => (
                <li key={p.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                  <span>
                    <Link to={lien(p.agent.id, 'paiement')} className="font-medium text-primary hover:underline">
                      {p.agent.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {LIBELLE_MODE_PAIEMENT_HRMS[p.mode]} · {p.banque ?? 'espèces'} · {p.compte ?? ''} · {p.devise}
                    </div>
                  </span>
                  <span className="text-xs text-muted-foreground">{dateHeureFr(p.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>
            Photos à valider <Badge variant="secondary">{data.photos.length}</Badge>
          </CardTitle>
          <CardDescription>Déposées par les agents eux-mêmes.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.photos.length ? (
            <Vide message="Aucune photo en attente." />
          ) : (
            <ul className="divide-y text-sm">
              {data.photos.map((p) => (
                <li key={p.agent.id} className="flex items-center gap-3 py-2">
                  <PhotoAgent agentId={p.agent.id} version={p.pieceId} nom={p.agent.nom} prenom="" taille="sm" />
                  <Link to={lien(p.agent.id, 'identite')} className="font-medium text-primary hover:underline">
                    {p.agent.nom}
                  </Link>
                  <span className="text-xs text-muted-foreground">{p.agent.fonction}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>
            Personnes à charge à valider <Badge variant="secondary">{data.charges.length}</Badge>
          </CardTitle>
          <CardDescription>Validées sur justificatif (acte de mariage, acte de naissance).</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.charges.length ? (
            <Vide message="Aucune déclaration en attente." />
          ) : (
            <ul className="divide-y text-sm">
              {data.charges.map((c) => (
                <li key={c.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
                  <span>
                    <Link to={lien(c.agent.id, 'famille')} className="font-medium text-primary hover:underline">
                      {c.agent.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {LIBELLE_LIEN_CHARGE[c.lien]} · {c.nom}
                      {c.pieceId ? '' : ' · sans justificatif'}
                    </div>
                  </span>
                  <span className="text-xs text-muted-foreground">{dateHeureFr(c.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RapportImport({ r }: { r: RapportImportHrms }) {
  const telecharger = async () => {
    try {
      await telechargerAnomaliesImportHrms(r.id, r.dateReference);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Card data-testid="rapport-import-hrms">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          {r.simulation ? 'Contrôle (simulation) : rien n’a été écrit' : 'Importation exécutée'}
          <Button size="sm" variant="outline" onClick={telecharger} disabled={!r.anomalies.length}>
            <Download className="size-4" /> Anomalies (Excel)
          </Button>
        </CardTitle>
        <CardDescription>
          {r.fichier} · date de référence {dateFr(r.dateReference)} · {dateHeureFr(r.createdAt)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Kpi label="Lignes lues" valeur={r.lignes} />
          <Kpi label="Agents créés" valeur={r.crees} accent={r.crees ? 'ok' : undefined} />
          <Kpi label="Agents mis à jour" valeur={r.misAJour} />
          <Kpi label="Lignes rejetées" valeur={r.rejetees} detail="anomalies bloquantes" accent={r.rejetees ? 'critique' : 'ok'} />
          <Kpi label="Anomalies" valeur={r.anomalies.length} detail={Object.entries(r.parCode).map(([c, n]) => `${c} ${n}`).join(' · ') || 'aucune'} accent={r.anomalies.length ? 'alerte' : 'ok'} />
        </div>
        {r.anomalies.length > 0 && (
          <div className="max-h-96 overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ligne</TableHead>
                  <TableHead>Matricule</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.anomalies.map((a, i) => (
                  <TableRow key={i} className={a.bloquante ? 'text-destructive' : ''}>
                    <TableCell>{a.ligne}</TableCell>
                    <TableCell>{a.matricule ?? '—'}</TableCell>
                    <TableCell>
                      {a.code}
                      {a.bloquante && <Badge variant="destructive" className="ml-1 text-[10px]">bloquante</Badge>}
                    </TableCell>
                    <TableCell>{a.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ImportHrms() {
  const actions = useActionsHrms();
  const historique = useImportsHrms();
  const [fichier, setFichier] = useState<File | null>(null);
  const [dateReference, setDateReference] = useState('2026-12-31');
  const [rapport, setRapport] = useState<RapportImportHrms | null>(null);
  const [ancien, setAncien] = useState<string | undefined>();
  const rapportAncien = useRapportImportHrms(ancien);
  const champ = useRef<HTMLInputElement | null>(null);
  const importer = async (simulation: boolean) => {
    if (!fichier) return;
    try {
      const r = await actions.importerHrms.mutateAsync({ fichier, simulation, dateReference: dateReference || undefined });
      setRapport(r);
      setAncien(undefined);
      if (!simulation) toast.success(`${r.crees} agent(s) créé(s), ${r.misAJour} mis à jour, ${r.rejetees} ligne(s) rejetée(s).`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const modele = async () => {
    try {
      await telechargerModeleHrms();
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Importer le fichier HRMS</CardTitle>
          <CardDescription>
            Classeur au format du fichier HRMS (feuille « HRMS », en-tête en ligne 2, un agent par ligne). Le matricule (colonne ID) rapproche les agents du référentiel : les agents absents sont créés, les autres mis à jour (nom, fonction, grade, catégorie, direction, région, contrat, identité, CNSS). Contrôlez d'abord (rien n'est écrit), puis écrivez ; chaque rapport d'anomalies est conservé.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="imp-date">Date de référence</Label>
            <Input id="imp-date" type="date" value={dateReference} onChange={(e) => setDateReference(e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="imp-fichier">Fichier HRMS (.xlsx)</Label>
            <Input id="imp-fichier" ref={champ} type="file" accept=".xlsx" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} data-testid="champ-import-hrms" />
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={modele}>
              Modèle
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={!fichier || actions.importerHrms.isPending} onClick={() => importer(true)}>
              Contrôler
            </Button>
            <Button type="button" size="sm" disabled={!fichier || actions.importerHrms.isPending} onClick={() => importer(false)}>
              <FileUp className="size-4" /> Écrire
            </Button>
          </div>
        </CardContent>
      </Card>
      {rapport && <RapportImport r={rapport} />}
      {ancien && rapportAncien.data && <RapportImport r={rapportAncien.data} />}
      <Card>
        <CardHeader>
          <CardTitle>Historique des importations</CardTitle>
          <CardDescription>Contrôles et écritures, avec le nombre d'anomalies de chaque rapport.</CardDescription>
        </CardHeader>
        <CardContent>
          {historique.isLoading ? (
            <Chargement />
          ) : !historique.data?.length ? (
            <Vide message="Aucune importation pour le moment." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Lignes</TableHead>
                  <TableHead>Créés / mis à jour / rejetés</TableHead>
                  <TableHead>Anomalies</TableHead>
                  <TableHead className="hidden md:table-cell">Par</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historique.data.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{dateHeureFr(i.createdAt)}</TableCell>
                    <TableCell>
                      <button type="button" className="text-primary hover:underline" onClick={() => { setRapport(null); setAncien(i.id); }}>
                        {i.fichier}
                      </button>
                      <div className="text-xs text-muted-foreground">référence {dateFr(i.dateReference)}</div>
                    </TableCell>
                    <TableCell>{i.simulation ? <Badge variant="outline">contrôle</Badge> : <Badge>écriture</Badge>}</TableCell>
                    <TableCell>{i.lignes}</TableCell>
                    <TableCell>
                      {i.crees} / {i.misAJour} / {i.rejetees}
                    </TableCell>
                    <TableCell className={i.anomalies ? 'text-amber-700' : 'text-emerald-700'}>{i.anomalies}</TableCell>
                    <TableCell className="hidden md:table-cell">{i.auteur ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ImportPhotos() {
  const actions = useActionsHrms();
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [resultat, setResultat] = useState<{ deposees: Array<{ fichier: string; matricule: string; agent: string }>; ignorees: Array<{ fichier: string; motif: string }> } | null>(null);
  const champ = useRef<HTMLInputElement | null>(null);
  const importer = async () => {
    if (!fichiers.length) return;
    try {
      const r = await actions.importerPhotos.mutateAsync(fichiers);
      setResultat(r);
      toast.success(`${r.deposees.length} photo(s) déposée(s), ${r.ignorees.length} ignorée(s).`);
      setFichiers([]);
      if (champ.current) champ.current.value = '';
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Photos des agents en lot</CardTitle>
        <CardDescription>Fichiers JPEG ou PNG nommés par matricule (« F-0021.jpg ») : chaque photo est rattachée au dossier de l'agent et validée d'office. Les fichiers sans matricule reconnu sont ignorés et listés.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="photos-lot">Photos</Label>
            <Input id="photos-lot" ref={champ} type="file" accept="image/png,image/jpeg" multiple onChange={(e) => setFichiers(Array.from(e.target.files ?? []))} data-testid="champ-photos-lot" />
          </div>
          <Button type="button" disabled={!fichiers.length || actions.importerPhotos.isPending} onClick={importer}>
            <Images className="size-4" /> Importer {fichiers.length ? `(${fichiers.length})` : ''}
          </Button>
        </div>
        {resultat && (
          <div className="grid gap-4 md:grid-cols-2 text-sm">
            <div>
              <div className="font-medium">Déposées ({resultat.deposees.length})</div>
              <ul className="list-inside list-disc text-muted-foreground">
                {resultat.deposees.map((d) => (
                  <li key={d.fichier}>
                    {d.fichier} → {d.agent} ({d.matricule})
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-medium">Ignorées ({resultat.ignorees.length})</div>
              <ul className="list-inside list-disc text-amber-800">
                {resultat.ignorees.map((d) => (
                  <li key={d.fichier}>
                    {d.fichier} : {d.motif}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
